import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Psychology,
  PlayArrow,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { User } from '../types';

interface AIModelBuilderProps {
  user: User;
}

const AIModelBuilder: React.FC<AIModelBuilderProps> = ({ user }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [modelType, setModelType] = useState('anomaly_detection');
  const [componentId, setComponentId] = useState('');
  const [algorithm, setAlgorithm] = useState('IsolationForest');
  const [trainingDataRange, setTrainingDataRange] = useState('30');
  const [features, setFeatures] = useState<string[]>([]);
  const [hyperparameters, setHyperparameters] = useState<Record<string, any>>({});
  const [training, setTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);

  const modelTypes = [
    { value: 'anomaly_detection', label: 'Anomaly Detection' },
    { value: 'predictive_maintenance', label: 'Predictive Maintenance' },
    { value: 'health_scoring', label: 'Health Scoring' },
  ];

  const algorithms = [
    { value: 'IsolationForest', label: 'Isolation Forest' },
    { value: 'Autoencoder', label: 'Autoencoder' },
    { value: 'OneClassSVM', label: 'One-Class SVM' },
    { value: 'RandomForest', label: 'Random Forest' },
    { value: 'LSTM', label: 'LSTM' },
  ];

  const dataRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' },
  ];

  const availableFeatures = [
    'temperature', 'vibration', 'pressure', 'current', 'voltage',
    'speed', 'load', 'efficiency', 'power', 'torque'
  ];

  const steps = [
    'Select Model Type',
    'Choose Component',
    'Configure Algorithm',
    'Select Features',
    'Set Parameters',
    'Train Model',
    'Validate & Deploy',
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFeatureToggle = (feature: string) => {
    setFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handleStartTraining = async () => {
    setTraining(true);
    setTrainingProgress(0);
    
    // Simulate training progress
    const interval = setInterval(() => {
      setTrainingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTraining(false);
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              What type of AI model do you want to create?
            </Typography>
            <Grid container spacing={2}>
              {modelTypes.map((type) => (
                <Grid item xs={12} sm={4} key={type.value}>
                  <Paper
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: modelType === type.value ? '2px solid #1976d2' : '1px solid #ddd',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => setModelType(type.value)}
                  >
                    <Typography variant="h6">{type.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {type.value === 'anomaly_detection' && 'Detect unusual patterns in sensor data'}
                      {type.value === 'predictive_maintenance' && 'Predict when maintenance is needed'}
                      {type.value === 'health_scoring' && 'Calculate component health scores'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Which component should this model analyze?
            </Typography>
            <TextField
              fullWidth
              label="Component ID"
              value={componentId}
              onChange={(e) => setComponentId(e.target.value)}
              helperText="Enter the component ID or select from the list"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose the algorithm for your model
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Algorithm</InputLabel>
              <Select
                value={algorithm}
                label="Algorithm"
                onChange={(e) => setAlgorithm(e.target.value)}
              >
                {algorithms.map((algo) => (
                  <MenuItem key={algo.value} value={algo.value}>
                    {algo.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select the sensor features to use for training
            </Typography>
            <Grid container spacing={1}>
              {availableFeatures.map((feature) => (
                <Grid item key={feature}>
                  <Chip
                    label={feature}
                    onClick={() => handleFeatureToggle(feature)}
                    color={features.includes(feature) ? 'primary' : 'default'}
                    variant={features.includes(feature) ? 'filled' : 'outlined'}
                  />
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Selected features: {features.length > 0 ? features.join(', ') : 'None'}
            </Typography>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure training parameters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Training Data Range</InputLabel>
                  <Select
                    value={trainingDataRange}
                    label="Training Data Range"
                    onChange={(e) => setTrainingDataRange(e.target.value)}
                  >
                    {dataRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model Name"
                  defaultValue={`${modelType}_${componentId}_${Date.now()}`}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Train your AI model
            </Typography>
            {!training ? (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Ready to train your {modelType} model for component {componentId}
                </Alert>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={handleStartTraining}
                  size="large"
                >
                  Start Training
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Training in progress...
                </Typography>
                <LinearProgress variant="determinate" value={trainingProgress} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {trainingProgress}% complete
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 6:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Model validation and deployment
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Model training completed successfully!
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Typography variant="body2">Accuracy: 94.5%</Typography>
                  <Typography variant="body2">Precision: 92.1%</Typography>
                  <Typography variant="body2">Recall: 96.8%</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Deployment Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle color="success" />
                    <Typography variant="body2">Model deployed successfully</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        AI Model Builder
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Create and train AI models for your components using our no-code interface
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {activeStep < steps.length - 1 && (
              <Button onClick={handleNext}>
                Next
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button variant="contained" startIcon={<CheckCircle />}>
                Complete
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AIModelBuilder;





